import SegmentLoadingButton from 'ui-component/Segment/SegmentLoadingButton';
import { FormControl, Typography, GridLegacy } from '@mui/material';
import { timeZones } from 'utils/dates';
import { useSelector } from 'store';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { FieldValues, useForm } from 'react-hook-form';

import { useGetOrgQuery, useUpdateOrgMutation } from 'store/slices/apiV1/org';
import { HookFormAutocomplete } from 'ui-component/HookFormComponents';
import SegmentButton from 'ui-component/Segment/SegmentButton';

import { useEffect, useState } from 'react';
import { handleErr } from 'utils/functions';
import CardSectionWithAction from 'ui-component/cards/CardSectionWithAction';
import { ALL_APPS } from 'constants/appConstants';
import { appPermissionAccessLevels } from 'types/apps';
import { gridSpacing } from 'constants/themeConstants';
import useSnackbar from 'hooks/useSnackbar';

const DateTimePreferences = () => {
  const { dispatchSuccessSnackbar, dispatchErrorSnackbar } = useSnackbar();

  const { activeOrgId } = useSelector((state) => state.org);

  const { data: org, isLoading: isLoadingOrg } = useGetOrgQuery(
    activeOrgId ?? skipToken
  );
  const [updateOrg, { isLoading: isUpdatingOrg }] = useUpdateOrgMutation();
  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, errors },
  } = useForm<FieldValues>({
    defaultValues: {
      timezone: org?.timezone,
      bomMissingCustomIdWarning: org?.bomMissingCustomIdWarning,
      bomUploadCustomId: org?.bomUploadCustomId,
      bomClearCustomIdOnAltUnapprove: org?.bomClearCustomIdOnAltUnapprove,
      bomAddCustomIdOnAltApprove: org?.bomAddCustomIdOnAltApprove,
      bomTransferCustomIdOnEdit: org?.bomTransferCustomIdOnEdit,
    },
  });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (org) {
      reset();
    }
  }, [isLoadingOrg]);

  const onSubmit = async (payload: FieldValues) => {
    if (isDirty) {
      try {
        await updateOrg({
          orgId: activeOrgId!,
          payload,
        }).unwrap();
        dispatchSuccessSnackbar('Preferences updated');
        setIsEditing(false);
      } catch (err) {
        handleErr(err, (errMessage: string) => {
          dispatchErrorSnackbar(errMessage);
        });
      }
    } else {
      setIsEditing(false);
    }
  };

  const actionButton = isEditing ? (
    <SegmentLoadingButton
      type="submit"
      variant="contained"
      color="primary"
      size="medium"
      loading={isUpdatingOrg}
      permissionScope={{
        app: ALL_APPS.ORG_SETTINGS.id,
        accessLevel: appPermissionAccessLevels.edit,
      }}
    >
      {isDirty ? 'Save' : 'Cancel'}
    </SegmentLoadingButton>
  ) : (
    <SegmentButton
      type="button"
      variant="contained"
      onClick={() => setIsEditing(true)}
      sx={{ float: 'right' }}
      permissionScope={{
        app: ALL_APPS.ORG_SETTINGS.id,
        accessLevel: appPermissionAccessLevels.edit,
      }}
    >
      Edit
    </SegmentButton>
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <GridLegacy container spacing={gridSpacing}>
        <GridLegacy item xs={12}>
          <CardSectionWithAction
            contents={
              <FormControl fullWidth>
                <GridLegacy container spacing={gridSpacing}>
                  <GridLegacy item xs={12}>
                    <HookFormAutocomplete<string>
                      name="timezone"
                      control={control}
                      label="Time Zone"
                      errors={errors}
                      getOptionLabel={(option) => option.replace(/[/_]/g, ' ')}
                      options={timeZones}
                      disabled={!isEditing}
                    />
                    <Typography sx={{ mt: 2 }}>
                      Daily notifications and workflow automations will be
                      scheduled based on business hours for the time zone that
                      you select here. We recommend selecting the time zone in
                      which the majority of your team members are located.
                    </Typography>
                  </GridLegacy>
                </GridLegacy>
              </FormControl>
            }
            action={actionButton}
            title="Date & Time"
          />
        </GridLegacy>
      </GridLegacy>
    </form>
  );
};

export default DateTimePreferences;
